/**
 * rambler.ru — stok ve site kullanım paneli (R-RAMBLER).
 *
 * DELIBERATELY NOT the shared MailcomPage template. That page's model is alias
 * inventory (mint 9 aliases per parent, renew, wipe); rambler has no aliases,
 * one unit per mailbox, and its scarce resource is the (mailbox, site) right.
 *
 * What this page answers, in the operator's words:
 *   1. kaç stok var (toplam / satılabilir / kiralamada),
 *   2. stokların %kaçı hangi site için kullanıldı (kalıcı yanma),
 *   3. kaç mailbox hiç yanmadı — yani kaçı için hâlâ her site açık.
 *
 * Visual language: the admin console's own primitives (card / kpi-grid / stat /
 * tbl / badge / btn), so the page sits inside the shell instead of beside it.
 */
import { useCallback, useEffect, useState } from 'react';

import { useCan } from '../../session/can.js';
import { BulkImportModal } from '../mailcom/BulkImportModal.js';
import {
  availableShare,
  fetchRamblerSiteUsage,
  type RamblerSiteUsage,
  type RamblerSiteUsageRow,
} from './siteUsage.js';

type State =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ok'; readonly data: RamblerSiteUsage }
  | { readonly kind: 'error'; readonly message: string };

export function RamblerPage(): JSX.Element {
  const [state, setState] = useState<State>({ kind: 'loading' });
  // 2026-09-15 audit: the import path was unreachable for rambler (the shared
  // modal was mounted only by the alias-model page, which nothing mounted for
  // rambler). Mount it here so the remaining accounts can be imported.
  const canManage = useCan('provider:manage_inventory');
  const [showImport, setShowImport] = useState(false);

  const load = useCallback(async (signal?: AbortSignal) => {
    setState({ kind: 'loading' });
    const res = await fetchRamblerSiteUsage(signal);
    if (res.kind === 'ok') {
      setState({ kind: 'ok', data: res.data });
      return;
    }
    if (res.kind === 'auth_required') {
      setState({ kind: 'error', message: 'Oturum gerekli — lütfen admin girişi yapın.' });
      return;
    }
    if (res.kind === 'forbidden') {
      setState({ kind: 'error', message: 'Yetki yok: provider:inspect izni gerekiyor.' });
      return;
    }
    if (res.kind === 'network_error') {
      setState({ kind: 'error', message: 'Bağlantı hatası: ' + res.message });
      return;
    }
    setState({ kind: 'error', message: 'İstek başarısız (' + res.status + ': ' + res.reason + ').' });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  return (
    <section className="rambler-dashboard" data-testid="rambler-dashboard">
      <header className="rambler-head">
        <div>
          <h1>rambler.ru — stok ve site kullanımı</h1>
          <p>
            Her satır bir mailbox'tır (alias üretilmez). Satış hakkı{' '}
            <strong>(mailbox, site)</strong> bazındadır: posta gelmezse yalnız o
            müşteriye kapanır ve başka müşteriye satılabilir; posta gelirse çift
            herkese yanar. Mailbox, yanmadığı her site için satılabilir kalır.
          </p>
        </div>
        <div className="rambler-head-actions">
          {state.kind === 'ok' && (
            <span className="badge badge-neutral">
              {state.data.accounts} hesap · {state.data.units.total} unit
            </span>
          )}
          {canManage ? (
            <button
              type="button"
              className="btn"
              onClick={() => setShowImport(true)}
              data-testid="rambler-import"
              title="email:password satırları — mailbox'ın kendisi unit olarak eklenir"
            >
              Import / TXT
            </button>
          ) : null}
          <button type="button" className="btn" onClick={() => void load()} data-testid="rambler-refresh">
            Yenile
          </button>
        </div>
      </header>
      {canManage ? (
        <BulkImportModal
          providerId="rambler"
          providerLabel="rambler.ru"
          open={showImport}
          onClose={() => setShowImport(false)}
          onCompleted={() => void load()}
        />
      ) : null}

      {state.kind === 'loading' && (
        <div className="card">
          <div className="card-body" data-testid="rambler-loading">Yükleniyor…</div>
        </div>
      )}

      {state.kind === 'error' && (
        <div className="card">
          <div className="card-body" role="alert" data-testid="rambler-error">
            <span className="badge badge-danger" style={{ marginRight: 8 }}>Hata</span>
            {state.message}
          </div>
        </div>
      )}

      {state.kind === 'ok' && (
        <>
          <div className="kpi-grid" style={{ marginBottom: 16 }}>
            <div className="card">
              <div className="card-body">
                <div className="stat" data-testid="kpi-total">
                  <div className="stat-label">Toplam stok</div>
                  <div className="stat-value">{state.data.units.total}</div>
                  <div className="stat-meta">mailbox (her biri bir unit)</div>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-body">
                <div className="stat" data-testid="kpi-available">
                  <div className="stat-label">Satılabilir</div>
                  <div className="stat-value">{state.data.units.available}</div>
                  <div className="stat-meta up">stokun %{availableShare(state.data.units)}</div>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-body">
                <div className="stat" data-testid="kpi-leased">
                  <div className="stat-label">Kiralamada</div>
                  <div className="stat-value">{state.data.units.leased}</div>
                  <div className="stat-meta">şu an müşteride</div>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-body">
                <div className="stat" data-testid="kpi-burned-pairs">
                  <div className="stat-label">Yanmış hak</div>
                  <div className="stat-value">{state.data.units.burnedPairs}</div>
                  <div className="stat-meta">(mailbox, site) çifti — kalıcı</div>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-body">
                <div className="stat" data-testid="kpi-burned-mailboxes">
                  <div className="stat-label">Yanmış mailbox</div>
                  <div className="stat-value">{state.data.units.mailboxesWithBurns}</div>
                  <div className="stat-meta">
                    {state.data.units.total - state.data.units.mailboxesWithBurns} mailbox hiç yanmadı
                  </div>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-body">
                <div className="stat" data-testid="kpi-accounts">
                  <div className="stat-label">Hesap</div>
                  <div className="stat-value">{state.data.accounts}</div>
                  <div className="stat-meta">import edilmiş rambler hesabı</div>
                </div>
              </div>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">
              <div>
                <div className="card-title">Site başına kullanım</div>
                <div className="card-subtitle">
                  Stokun yüzde kaçı hangi site için kullanıldı (posta gelince kalıcı yanar)
                </div>
              </div>
              <div className="card-header-actions">
                <span className="badge badge-neutral">{state.data.sites.length} site</span>
              </div>
            </div>
            {state.data.sites.length === 0 ? (
              <div className="card-body" data-testid="rambler-no-sites">
                <span className="badge badge-success" style={{ marginRight: 8 }}>Temiz</span>
                Henüz hiçbir site için yanma yok — tüm stok her site için kullanılabilir.
              </div>
            ) : (
              <table className="tbl" data-testid="rambler-site-table">
                <thead>
                  <tr>
                    <th>Site</th>
                    <th>Yanmış mailbox</th>
                    <th style={{ minWidth: 200 }}>Stok payı</th>
                    <th>Satıldı (posta gelmedi)</th>
                    <th>Son yanma</th>
                  </tr>
                </thead>
                <tbody>
                  {state.data.sites.map((row) => (
                    <tr key={row.site} data-testid={'site-row-' + row.site}>
                      <td className="t-mono">{row.site}</td>
                      <td>{row.burnedMailboxes}</td>
                      <td>
                        <div className="rambler-share">
                          <span className="rambler-bar" aria-hidden="true">
                            <span
                              className={'rambler-bar-fill' + shareClass(row)}
                              style={{ width: Math.min(100, row.shareOfStock) + '%' }}
                            />
                          </span>
                          <span className="badge badge-neutral" data-testid={'site-share-' + row.site}>
                            %{row.shareOfStock}
                          </span>
                        </div>
                      </td>
                      <td>{row.servedMailboxes}</td>
                      <td className="t-small t-tertiary">
                        {row.lastBurnedAt ? new Date(row.lastBurnedAt).toLocaleString('tr-TR') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="card">
            <div className="card-body">
              <div className="card-title" style={{ marginBottom: 6 }}>Kapasite</div>
              <p className="rambler-footnote" data-testid="rambler-capacity-note">
                {state.data.units.total} mailbox × sınırsız site hakkı. Şu an{' '}
                <strong>{state.data.units.available}</strong> mailbox satılabilir (%{availableShare(state.data.units)});
                yanmış çiftler yalnızca kendi sitesi için kapalı, mailbox'ın kendisi kapanmaz.
              </p>
              <a className="btn" href="/admin/rambler-control-plane/raw" data-testid="rambler-raw-link">
                Hesaplar &amp; ham kontrol düzlemi (hesap listesi, credential, readiness) →
              </a>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

/** Bar rengi: payı büyüdükçe dikkat çeksin (yeşil → sarı → kırmızı). */
function shareClass(row: RamblerSiteUsageRow): string {
  if (row.shareOfStock >= 50) return ' is-high';
  if (row.shareOfStock >= 20) return ' is-mid';
  return '';
}
